/**
 * deployment-row.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { makeLocation, makeDeploymentSectionPath } from 'utils/routing';
import { renderForMobileContext } from 'utils/app-context';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import RecordMenuContext from 'contexts/record-context-menu/record-menu.context';
import Icon from 'components/icon';
import IconPatrolDetection from 'components/icon-patrol-detection';
import ReviewStatusIconRecordHandler from 'components/record-status-icons/review/review-status-icon-record-handler';
import ReviewStatusIconRecordSuper from 'components/record-status-icons/review/review-status-icon-record-super';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import { ViewingMapContext } from 'components/tracking/map/viewing-map-context';
import {
  getOdorSummary,
  getPatrolSummary,
  shrinkOptionalWidth,
} from '../helpers';
import Row from '../row/row';
import RowStatusChip from '../row/row-status-chip';


const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.sys.onSurface,
  },
  completionStatusChip: {
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      display: 'none'
    }
  },
  completionStatusIcon: {
    display: 'none',
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      display: 'flex',
      alignItems: 'center'
    }
  },
  completionChip: {
    minWidth: 110,
  },
}));


const DeploymentRow = React.memo(function (props) {
  const {
    id,
    basePath,
    isSelected,
    onSelectionChange,
    onViewReport,
    isRecordTrackingRelevant,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation('general');
  const { user } = React.useContext(UserContext);
  const { setMapParams, setIsDialogOpen } = React.useContext(ViewingMapContext);
  const recordContextMenu = React.useContext(RecordMenuContext);
  const recordStore = useRecordStore();
  const deploymentRecord = recordStore.useRecord(Types.Record.DEPLOYMENT, id).ornull;

  if (!deploymentRecord) return null;
  

  const deployment = deploymentRecord.get();
  const workMode = deployment.workMode;
  const workModeName = workMode && Types.WorkMode.getName(t, workMode);
  const customCompare = `drs:${deployment.review?.status}-irtr:${isRecordTrackingRelevant}`;
  const isReportDisabled = !deployment.isComplete;
  const reportDisabledReason = !deployment.isComplete
    ? <>Reports can only be created for<br />completed deployments</>
    : null;
  const trackingRecord = deployment.patrol?.patrolTypes?.[0]?.tracking;
  const hasTrack = user.role.isSupervisor && trackingRecord?.hasTrack;


  // route link
  const to = makeLocation(
    basePath,
    'deployment',
    'view',
    { deploymentId: id }
  );
  const routeLink = <RouterLink to={to} className='absolute-fill record-link' />;


  // completion status
  const completionStatusId = deployment.isComplete ? 'complete' : 'incomplete';
  const completionStatusLabel = <>
    <Icon type={completionStatusId} />
    {deployment.isComplete ? "Complete" : "Incomplete"}
  </>

  
  // review status (supervisor logic)
  const reviewStatusId = deployment.review?.status === Types.ReviewStatus.NOT_REVIEWED
    ? 'not-reviewed'
    : 'reviewed';
  const reviewStatusText = deployment.review?.status === Types.ReviewStatus.NOT_REVIEWED
    ? 'Not Reviewed'
    : 'Reviewed';
  const reviewStatusLabel = <>
    <Icon type={reviewStatusId} />
    {reviewStatusText}
  </>


  // menu items
  const menuItems = [
    ...(!renderForMobileContext() ? [
      // view
      {
        title: 'View',
        iconType: 'view',
        component: RouterLink,
        to: makeLocation(
          basePath,
          'deployment',
          'view',
          { deploymentId: deployment.id },
          null),
        isDefaultItem: !user.role.status.isActive || deployment.isComplete
      },

      // edit
      user.role.isHandler ? {
        title: 'Edit',
        iconType: 'edit',
        component: RouterLink,
        to: makeLocation(
          basePath,
          'deployment',
          'edit',
          {
            deploymentId: deployment.id,
            editSectionId: makeDeploymentSectionPath(Types.DeploymentSection.GENERAL),
          },
          null),
        isDefaultItem: !deployment.isComplete,
        disabled: !user.role.status.isActive,
        disabledReason: t('readOnlyAccount')
      } : {},
        hasTrack
          ? {
            title: 'Tracking Map',
            iconType: 'location',
            onSelect: () => {
              setMapParams(TRACK_TYPE.DEPLOYMENT, deployment.id, trackingRecord.id);
              setIsDialogOpen(true);
            }
          } : {}
      ] : []),

    // view report
    {
      title: 'View Report',
      iconType: 'report',
      onSelect: () => onViewReport(Types.RecordGroup.DEPLOYMENT, [id]),
      disabled: isReportDisabled,
      disabledReason: reportDisabledReason
    },

    // delete
    user.role.isHandler ? {
      title: 'Delete ...',
      iconType: 'delete',
      onSelect: () => recordContextMenu.deleteDeployment(deployment.id),
      disabled: !user.role.status.isActive || isRecordTrackingRelevant,
      disabledReason: !user.role.status.isActive
        ? t('readOnlyAccount')
        : isRecordTrackingRelevant
          ? 'Tracking is active for this deployment'
          : ''
    } : {},
  ];
  
  const detailToShow = () => {
    if (deployment.fulfillmentState === Types.FulfillmentState.DogNotDeployed) {
      return `${workModeName}: Dog Not Deployed`
    } else if (deployment.fulfillmentState === Types.FulfillmentState.CancelledEnroute) {
      return `${workModeName}: Request Cancelled`
    } else {
      return `${workModeName}: ${workMode === Types.WorkMode.PATROL
    ? getPatrolSummary(Types.Record.DEPLOYMENT, deployment.patrol.patrolTypes, t)
    : getOdorSummary(deployment.detection.environments)}`
    }
  }

  return (
    <>
      {deployment &&
      <Row
        data-testid={id}
        data-testtype='record-deployment'
        recordType={Types.Record.DEPLOYMENT}
        className='content'
        routeLink={routeLink}

        selection={{
          type: Types.RecordGroup.DEPLOYMENT,
          id,
          isSelected,
          isDisabled: isReportDisabled,
          disabledReason: reportDisabledReason,
          onChange: onSelectionChange
        }}

        icon={<IconPatrolDetection
          workMode={deployment.workMode}
          patrolType={deployment.workMode === Types.WorkMode.PATROL && deployment.patrol.patrolTypes.length > 0
            ? deployment.patrol.patrolTypes[0].type
            : Types.Patrol.OTHER}
          fulfillmentState={deployment.fulfillmentState}
          className={classes.icon}
        />}
        title={deployment.caseNumber}
        detail={detailToShow()}

        optional={
          <div className={`flex-hc record-status`}>
            {/* review */}
            <div className='review-status'>
              {/* HANDLER */}
              {user.role.isHandler &&
              <>
                <ReviewStatusIconRecordHandler
                  reviewStatus={deployment.review?.status}
                  isAvailable={deployment.isComplete}
                  to={to}
                />
              </>}
              {/* SUPER */}
              {(user.role.isSupervisor && deployment.isComplete) &&
              <>
                <div className='status-desktop'>
                  <RowStatusChip
                    className={`chip-${reviewStatusId} chip-font-${reviewStatusId}`}
                    label={reviewStatusLabel}
                    to={to}
                  />
                </div>
                <div className='status-mobile'>
                  <ReviewStatusIconRecordSuper
                    reviewStatus={deployment.review?.status}
                    isAvailable={deployment.isComplete}
                    to={to}
                  />
                </div>
              </>}
            </div>

            {/* completion chip - desktop */}
            <span className='status-desktop'>
              <RowStatusChip
                className={`chip-${completionStatusId} chip-font-${completionStatusId} ${classes.completionChip}`}
                label={completionStatusLabel}
              />
            </span>
            {/* completion icon - mobile */}
            <span className='status-mobile'>
              <Icon
                type={completionStatusId}
                className={`icon-${completionStatusId}`}
              />
            </span>
          </div>}

        menu={{
          ariaLabel: 'deployment record options',
          className: '',
          items: menuItems
        }}

        customCompare={customCompare}
      />}
    </>
  )
}, (prev, next) => {
  return (
    prev.id === next.id
    && prev.isSelected === next.isSelected
    && prev.isSelectDisabled === next.isSelectDisabled
    && prev.isRecordTrackingRelevant === next.isRecordTrackingRelevant
  );
});

export default DeploymentRow;